import type { ReactNode } from 'react';
import { blockTab, pageTab, pillSoft, pillSolid, segOutline, segSmall, subTab, sx, underlineTab, underlineTabCount, helpTab } from './designStyles';

export type TabVariant = 'page' | 'block' | 'underline' | 'help' | 'pillSoft' | 'pillSolid' | 'sub' | 'segSmall' | 'segOutline';

const STYLE: Record<TabVariant, (on: boolean) => string> = {
  page: pageTab,
  block: blockTab,
  underline: underlineTab,
  help: helpTab,
  pillSoft,
  pillSolid,
  sub: subTab,
  segSmall,
  segOutline,
};

/**
 * The row each variant sits in. Underline and help rows are the design's own (Worklist
 * T1375, Help T8551); the rest are a plain row, and a page passes its own container
 * string (rowStyle) copied from the design where it differs.
 */
const ROW: Record<TabVariant, string> = {
  page: 'display:flex;align-items:center;gap:0 22px;flex-wrap:wrap;min-width:0;border-bottom:1px solid rgba(34,39,46,.12);',
  block: 'display:flex;align-items:center;gap:0 18px;flex-wrap:wrap;min-width:0;border-bottom:1px solid rgba(34,39,46,.12);',
  underline: 'position:relative;display:flex;align-items:center;flex:0 0 auto;gap:0 14px;flex-wrap:nowrap;white-space:nowrap;overflow-x:auto;scrollbar-width:none;margin:0 0 12px;min-width:0;border-bottom:1px solid rgba(34,39,46,.12);',
  help: 'flex:0 0 auto;display:flex;align-items:center;gap:22px;min-width:0;border-bottom:1px solid rgba(34,39,46,.12);',
  pillSoft: 'display:flex;gap:6px;flex-wrap:wrap;',
  pillSolid: 'display:flex;gap:6px;flex-wrap:wrap;',
  sub: 'display:flex;gap:6px;flex-wrap:wrap;',
  segSmall: 'display:inline-flex;gap:2px;flex-wrap:wrap;',
  segOutline: 'display:inline-flex;gap:6px;flex-wrap:wrap;',
};

export interface TabItem<K extends string> {
  key: K;
  label: ReactNode;
  /** Underline tabs: a count beside the label, hidden at zero. */
  count?: number;
}

/**
 * One tab row in any of the design's forms. Keyboard: Left and Right move between tabs,
 * like a tablist. The row's container style can be replaced with `rowStyle` (a design
 * style string) where a page lays its tabs out differently.
 */
export function Tabs<K extends string>({
  items,
  value,
  onChange,
  variant = 'page',
  label,
  rowStyle,
}: {
  items: readonly TabItem<K>[];
  value: K;
  onChange: (k: K) => void;
  variant?: TabVariant;
  /** Accessible name of the tab row. */
  label: string;
  rowStyle?: string;
}) {
  const move = (i: number) => {
    const next = items[(i + items.length) % items.length];
    if (next) onChange(next.key);
  };
  return (
    <div role="tablist" aria-label={label} style={sx(rowStyle ?? ROW[variant])}>
      {items.map((t, i) => {
        const on = t.key === value;
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            className="dtab"
            style={sx(STYLE[variant](on))}
            onClick={() => onChange(t.key)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') {
                e.preventDefault();
                move(i + 1);
              } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                move(i - 1);
              }
            }}
          >
            {t.label}
            {variant === 'underline' && t.count !== undefined && <span style={sx(underlineTabCount(t.count, on))}>{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
